/**
 * Profile visibility rules.
 *
 * Two independent gates decide what a viewer sees:
 *  1. `profileViewPrivacy` — whether the profile can be opened at all.
 *  2. the per-field `*Privacy` values — which rows survive inside an open profile.
 *
 * The backend remains the authority (`UserProfileResponse.isAccessible`); these
 * helpers apply the same rules to whatever it returns so a Public profile still
 * hides fields the owner marked Buddies-only or Only-me.
 */
import { PRIVACY_LEVEL, toPrivacyLevel } from '../constants/privacy';

export interface ViewerContext {
  /** The viewer is the profile owner. */
  isSelf?: boolean;
  /** The viewer and the owner are connected buddies. */
  isBuddy?: boolean;
}

const isAllowed = (
  privacy: string | number | null | undefined,
  { isSelf = false, isBuddy = false }: ViewerContext,
): boolean => {
  if (isSelf) return true;
  const level = toPrivacyLevel(privacy);
  // An absent setting means the backend never restricted the value.
  if (level === undefined) return true;
  if (level === PRIVACY_LEVEL.PUBLIC) return true;
  if (level === PRIVACY_LEVEL.BUDDIES) return isBuddy;
  return false;
};

/** Whether the viewer may open this profile at all. */
export const canViewProfile = (
  profileViewPrivacy: string | number | null | undefined,
  viewer: ViewerContext,
): boolean => isAllowed(profileViewPrivacy, viewer);

/** Whether the viewer may see one field of an already-open profile. */
export const canViewField = (
  fieldPrivacy: string | number | null | undefined,
  viewer: ViewerContext,
): boolean => isAllowed(fieldPrivacy, viewer);

/**
 * Whether a field is restricted to anyone. Drives the lock badge the owner sees
 * next to their own Country / Date of Birth chips.
 */
export const isFieldLocked = (
  fieldPrivacy: string | number | null | undefined,
): boolean => {
  const level = toPrivacyLevel(fieldPrivacy);
  return level !== undefined && level !== PRIVACY_LEVEL.PUBLIC;
};

/** Why a profile could not be opened, so the UI can pick its message. */
export type ProfileRestrictionReason =
  | 'private'
  | 'buddiesOnly'
  | 'restricted'
  | 'unavailable';

export const getProfileRestrictionReason = (
  profileViewPrivacy: string | number | null | undefined,
  viewer: ViewerContext,
): ProfileRestrictionReason | null => {
  if (canViewProfile(profileViewPrivacy, viewer)) return null;
  return toPrivacyLevel(profileViewPrivacy) === PRIVACY_LEVEL.BUDDIES
    ? 'buddiesOnly'
    : 'private';
};

/**
 * Decides the restriction for a fetched profile. The backend is the
 * authority: an explicit `isAccessible: true` always wins (a buddy may open a
 * "Private" profile when the server says so), and the local rule only runs
 * when the payload omits the flag.
 */
export const resolveProfileRestriction = (
  isAccessible: boolean | null | undefined,
  profileViewPrivacy: string | number | null | undefined,
  viewer: ViewerContext,
): ProfileRestrictionReason | null => {
  if (isAccessible === true) return null;
  return getProfileRestrictionReason(profileViewPrivacy, viewer);
};
